/**
 * Calendar helpers on plain strings: dates `YYYY-MM-DD`, periods `YYYY-MM`.
 * All arithmetic is done in UTC so it never shifts with the host timezone.
 */

import { MONTHS_GENITIVE_SHORT_RU, monthNameRu } from './ru';

/** English month abbreviations (invoice templates, legacy formats). Screens use monthShort(). */
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const PERIOD_RE = /^(\d{4})-(\d{2})$/;

export function isIsoDate(value: string): boolean {
  const m = DATE_RE.exec(value);
  if (!m) return false;
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

export function isPeriod(value: string): boolean {
  const m = PERIOD_RE.exec(value);
  return !!m && Number(m[2]) >= 1 && Number(m[2]) <= 12;
}

/** Local calendar date of `now` on this machine (the app runs locally). */
export function todayIso(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function addDays(date: string, days: number): string {
  const t = new Date(`${date}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + days);
  return t.toISOString().slice(0, 10);
}

/** Whole days from a to b (b − a). */
export function dayDiff(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

const MONTHS_PER_YEAR = 12;

/** Whole months from `from` to `to`: a day earlier in the month does not count (negative when `to` is earlier). */
export function wholeMonthsBetween(from: string, to: string): number {
  const months = (Number(to.slice(0, 4)) - Number(from.slice(0, 4))) * MONTHS_PER_YEAR + (Number(to.slice(5, 7)) - Number(from.slice(5, 7)));
  return Number(to.slice(8, 10)) < Number(from.slice(8, 10)) ? months - 1 : months;
}

/** The same day of the month `months` later; a day that month lacks lands on its last day. */
export function addMonthsToDate(date: string, months: number): string {
  const period = addMonths(periodOf(date), months);
  const lastDay = periodEnd(period).slice(8, 10);
  const day = date.slice(8, 10);
  return `${period}-${day < lastDay ? day : lastDay}`;
}

export function periodOf(date: string): string {
  return date.slice(0, 7);
}

export function periodStart(period: string): string {
  return `${period}-01`;
}

export function periodEnd(period: string): string {
  const [y, m] = period.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${period}-${String(last).padStart(2, '0')}`;
}

export function addMonths(period: string, months: number): string {
  const [y, m] = period.split('-').map(Number) as [number, number];
  const index = y * 12 + (m - 1) + months;
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  return `${year}-${String(month).padStart(2, '0')}`;
}

export function previousPeriod(period: string): string {
  return addMonths(period, -1);
}

export function nextPeriod(period: string): string {
  return addMonths(period, 1);
}

/** Inclusive list of periods between two periods. */
export function periodsBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let p = from; p <= to; p = nextPeriod(p)) out.push(p);
  return out;
}

/** "2026-10-02" -> "02 окт 2026"; empty → "—". */
export function formatDocDate(date: string | null | undefined): string {
  if (!date) return '—';
  return `${date.slice(8, 10)} ${MONTHS_GENITIVE_SHORT_RU[Number(date.slice(5, 7)) - 1]} ${date.slice(0, 4)}`;
}

/**
 * Invoice template dates: always English "02 Oct 2026", '' for empty — never Russian, so issued
 * PDFs stay byte-identical (decisions-log T8, L2).
 */
export function formatTemplateDate(date: string | null | undefined): string {
  if (!date) return '';
  return `${date.slice(8, 10)} ${MONTHS[Number(date.slice(5, 7)) - 1]} ${date.slice(0, 4)}`;
}

/** "2026-10-02" -> "2 окт" (dense tables). */
export function formatShortDate(date: string | null | undefined): string {
  if (!date) return '—';
  return `${Number(date.slice(8, 10))} ${MONTHS_GENITIVE_SHORT_RU[Number(date.slice(5, 7)) - 1]}`;
}

/** "2026-10-02" -> "2 окт 26" (tables that span years). */
export function formatShortDateYear(date: string | null | undefined): string {
  if (!date) return '—';
  return `${formatShortDate(date)} ${date.slice(2, 4)}`;
}

/** "2026-10" -> "октябрь 2026" (inside sentences: «за октябрь 2026»). */
export function formatPeriodInline(period: string): string {
  return `${monthNameRu(period)} ${period.slice(0, 4)}`;
}

/** "2026-10" -> "Октябрь 2026" (titles, selects). */
export function formatPeriod(period: string): string {
  const text = formatPeriodInline(period);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "01 окт 2026 – 31 окт 2026" as shown in the invoice editor. */
export function formatPeriodRange(start: string, end: string): string {
  return `${formatDocDate(start)} – ${formatDocDate(end)}`;
}
